import { cn } from "@/lib/utils";
import { ChevronDown, PlusIcon, Search } from "lucide-react";
import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Icon, { ICONS } from "../Core/Icons";
import { Button } from "../ui/button";
import { useNavigate, useSearchParams } from "react-router";
import { useProjectId } from "@/utils/utils";
import UserMenu from "./UserMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import ShareModal from "@/components/ShareModal";
import HireHumanModal from "../AssetsPanel/HireHumanModal";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";

interface Project {
  id: string;
  name: string;
  repoPath?: string;
}

interface Props {
  label?: string;
  hideCreateProject?: boolean;
  showProjectDropdown?: boolean;
}

const AppHeader: React.FC<Props> = React.memo((props) => {
  const { showProjectDropdown = false } = props;
  const navigate = useNavigate();
  const projects = useSelector(
    (state: any) => state.supabaseStore.projects
  ) as Record<string, Project>;
  const projectId = useProjectId();
  const project = projectId ? projects[projectId] : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showHireHumanModal, setShowHireHumanModal] = useState(false);

  // Sort projects alphabetically and filter based on search
  const sortedProjects = useMemo(() => {
    return Object.values(projects || {})
      .filter(
        (p: Project) =>
          p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (p.repoPath || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a: Project, b: Project) => a.name.localeCompare(b.name));
  }, [projects, searchQuery]);

  // Auto-select first project if no project is selected
  useEffect(() => {
    if (
      showProjectDropdown &&
      !projectId &&
      sortedProjects.length > 0 &&
      !window.location.pathname.startsWith("/project-create")
    ) {
      setSearchParams((prev) => {
        prev.set("project_id", sortedProjects[0].id);
        return prev;
      });
    }
  }, [projectId, sortedProjects, setSearchParams, showProjectDropdown]);

  // Register project dropdown with modal store
  useEffect(() => {
    if (isDropdownOpen) {
      useProjectModalStore.getState().addProjectModal("project-dropdown");
      return () => {
        useProjectModalStore.getState().removeProjectModal("project-dropdown");
      };
    }
  }, [isDropdownOpen]);

  const handleProjectSelect = (selectedProject: Project) => {
    setSearchParams((prev) => {
      prev.set("project_id", selectedProject.id);
      return prev;
    });
    setIsDropdownOpen(false);
    setSearchQuery(""); // Reset search when closing
  };

  return (
    <nav className="border-b px-2 shrink-0 flex items-center justify-between z-[2002] relative h-11">
      <div className="flex-row flex grow justify-between items-center ml-0 my-0">
        <div className="flex flex-1 grow items-center">
          <Icon
            icon={ICONS.TEMPO_LOGO_WHITE}
            className="flex grow-0 w-5 h-5 cursor-pointer mr-2 p-1.5"
            onClick={(e) => {
              if (
                Object.keys(projects).length === 0 ||
                window.location.pathname === "/"
              ) {
                return;
              }

              if (e.metaKey || e.ctrlKey) {
                window.open("/", "_blank");
                return;
              }

              navigate("/");
            }}
          />
          <div
            className={cn(
              "text-xs font-normal text-secondary-800",
              !props.label && "hidden"
            )}
          >
            {props.label}
          </div>
          <div className="flex flex-1 grow"></div>
        </div>
        {showProjectDropdown && (
          <>
            <Button
              variant="primary"
              size="sm"
              className="h-[28px] rounded-md bg-background-CTA mx-2 px-2 flex items-center"
              onClick={() => {
                navigate("/project-create/workflow-select");
              }}
            >
              <PlusIcon className="size-3.5" />
              <div className="text-xs font-normal ml-1">Create Project</div>
            </Button>
            <div className="flex items-center">
              <UserMenu />
            </div>
          </>
        )}
      </div>
      {showShareModal && (
        <ShareModal closeModal={() => setShowShareModal(false)} />
      )}
      {showHireHumanModal && (
        <HireHumanModal closeModal={() => setShowHireHumanModal(false)} />
      )}
    </nav>
  );
});

export default AppHeader;
